<template>
  <div class="flex  px-2 pb-6 justify-between gap-4">
    <div class="flex flex-col md:flex-row justify-between items-center gap-4 w-full">
      <div class="flex flex-row justify-between items-center gap-4 w-full">
        <ExportFetch exportEndpoint="/invoice/export" fileName="facturas"
          :date-range="isAdsUser()" :from-label="$t('export.range.invoiceFrom')" :to-label="$t('export.range.invoiceTo')"
          @export-started="handleExportStarted" @export-completed="handleExportCompleted"
          @export-failed="handleExportFailed" />

        <RouterLink to="new-bill">
          <ButtonWhite class="md:hidden flex items-center gap-2 text-[#385660] md:w-auto">
            {{ $t('bill.buttons.addNew') }}
            <PlusIcon />
          </ButtonWhite>
        </RouterLink>
      </div>
      <div class="flex flex-row justify-center lg:justify-end items-center md:gap-4 w-full lg:w-3/4">
        <RouterLink to="new-bill">
          <ButtonWhite class="hidden md:flex items-center gap-2 text-[#385660] md:w-full lg:w-auto">
            {{ $t('bill.buttons.addNew') }}
            <PlusIcon />
          </ButtonWhite>
        </RouterLink>
        <div class="w-full">

        </div>
      </div>

    </div>
  </div>
  <div class="py-4 px-2 overflow-x-auto">
    <TitlePrimary :title="$t('bill.title')" class="w-full md:w-auto" />
    <div class="md:hidden">
      <CardTable v-if="role == 'superadmin'" :fields="fields" :items="items.length > 0 ? [items[currentItemIndex]] : []"
        :listOptions="optionsList" />
      <CardTable v-else :fields="fields" :items="items.length > 0 ? [items[currentItemIndex]] : []" :showOptions="false"
        :listOptions="optionsList" />
      <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
        :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
        @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
    </div>
    <div class="hidden md:block">
      <TableComponent :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange" :fields="fields" :items="items" :showOptions="true"  :listOptions="optionsList" />

      <PaginateComponent :totalPages="totalPages" :paginationInfo="paginationInfo" :modelValue="currentPage" @update:modelValue="handlePageChange" />
    </div>
  </div>
</template>

<script setup>
import ExportFetch from '@/components/fetch/exportFetch.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import PlusIcon from '@/assets/svg/PlusIcon.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import { usePaginationInfo } from '@/composables/usePaginationInfo';

import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTableFilters } from '@/composables/useTableFilters';
import axios from 'axios';
import { useExportStore } from '@/stores/exportStore';
import { isAdsUser } from '@/services/userRoles';
import CardTable from '@/components/table/CardTable.vue';
import MobilePaginateComponent from '@/components/paginate/MobilePaginateComponent.vue';

const { t } = useI18n();
const role = localStorage.getItem('role');
const exportStore = useExportStore();

const props = defineProps({
  perPage: {
    type: Number,
    default: 10
  }
});

const perPages = ref(10);
const searchText = ref('');
const selectedFilterName = ref({ key: 'created_at' });

const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();
const isSearchActive = ref(false);
const currentItemIndex = ref(0);
const totalItems = ref(0);

const fields = [
  { key: "created_at", label: "Fecha" },
  { key: "type", label: "Luz/Gas" },
  { key: "invoice_number", label: "N. Factura" },
  { key: "total", label: "Importe" },
  { key: "file", label: "Documento" },
  // { key: "opciones", label: "Opciones" }
];

// --- filtros de cabecera ---
const filterDefinitions = computed(() => [
  { key: 'created_at', type: 'date' },
  {
    key: 'type',
    type: 'select',
    options: ['light', 'gas'].map((value) => ({ value, label: t(`common.requestTypes.${value}`) })),
  },
  { key: 'invoice_number', type: 'text' },
  { key: 'total', type: 'text' },
  { key: 'file', type: 'text' },
]);

const { filters, isFiltering, filterError, runSearch, onFilterChange } = useTableFilters({
  endpoint: () => (isAdsUser() ? '/invoice/admin/search' : '/invoice/user/search'),
  perPage: 10,
  translate: t,
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    items.value = (page.data || []).map((item) => ({
      ...item,
      created_at: new Date(item.created_at).toLocaleDateString(),
      total: parseFloat(item.total).toFixed(2),
      ...invoiceFileFields(item),
    }));
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    totalItems.value = page.total ?? 0;
    currentPage.value = page.current_page || 1;
    isSearchActive.value = true;
  },
  onCleared: () => {
    isSearchActive.value = false;
    currentPage.value = 1;
    getInvoiceList();
  },
});

const optionsList = ref([
  { key: '/forms/supplies', label: 'Alta de suministro' },
  { key: '/forms/account_change', label: 'Cambio de titular' },
  { key: '/forms/invoice', label: 'Solicitud de facturas' },
  { key: '/forms/change_ownership', label: 'Cambio de cuenta de suministro' },
  // Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
  // { key: '/forms/change_supplier', label: 'Cambio de comercializadora (mismo titular)' },
  { key: '/formsGAasAL/other_type', label: 'Otro tipo' }
])

// El documento se sirve por el endpoint de la factura, que lo resuelve en el
// disco local o en el legacy (ADS-0168); `is_available` avisa si no está.
const invoiceFileFields = (item) => ({
  file: item.file ? item.file : '',
  file_name: item.file ? String(item.file).split('/').pop() : '',
  is_available: item.is_available !== false,
  preview_url: `${import.meta.env.VITE_API_URL}/invoice/${item.id}/download?disposition=inline`,
  download_url: `${import.meta.env.VITE_API_URL}/invoice/${item.id}/download`,
});

const getInvoiceList = async () => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/invoice/admin/list`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        params: {
          per_page: perPages.value,
          page: currentPage.value
        }
      }
    )

    if (response.data?.value?.data) {
      items.value = response.data.value.data.map(item => ({
        ...item,
        created_at: new Date(item.created_at).toLocaleDateString(),
        total: parseFloat(item.total).toFixed(2),
        ...invoiceFileFields(item),
        type: item.type || item.supply?.type || ''
      }));
      totalPages.value = response.data.value.last_page || 1;
      setPaginationInfo(response.data.value);
      totalItems.value = response.data.value.total ?? 0;
      console.log('items table supplys bill:', items.value);
    }
    return response.data;

  } catch (error) {
    console.error('Error fetching invoice list:', error);
  }
}

const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    await getInvoiceList();
  }

};

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};

async function performSearch(searchEndpoint, perPage, page = 1) {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${searchEndpoint}`,
      {
        params: {
          query: searchText.value,
          per_page: props.perPage,
          page: page, // Usar página recibida como parámetro
          search_by: selectedFilterName.value.key
        }
      }
    );
    console.log('search-results', response.data);
  } catch (error) {
    console.log('search-error', error);
  }
}
defineExpose({ performSearch });

const handleExportStarted = () => {
  console.log('Exportación iniciada');
  exportStore.startExport('Exportando facturas...');
};

const handleExportCompleted = () => {
  console.log('Exportación completada');
  exportStore.completeExport();
};

const handleExportFailed = () => {
  console.log('Exportación fallida');
  exportStore.failExport('Error al exportar facturas');
};

onMounted(async () => {
  await getInvoiceList();
})

</script>
